import React, { useRef, useState } from 'react';
import { useDocuments } from './hooks/useDocuments';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './scss/DocumentVault.module.scss';
import { Box, Grid, Pagination, Stack, Tab, Tabs, Typography } from '@mui/material';
import UploadButton from '../../common/upload-button/UploadButton';
import FormUpload from '../../common/form-upload/FormUpload';
import DocumentListVault from './components/document-list-vault/DocumentListVault';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { useFileTypeDownload } from '../../common/file-type-download-modal/useFileTypeDownload';
import { useTranslation } from 'react-i18next';
import { selectAllEmployees } from '../../slices/employees/employeesSlice';
import { selectAllSuppliers } from '../../slices/suppliers/suppliersSlice';
import { selectAllCustomers } from '../../slices/customers/customersSlice';
import { selectBusinessProfileContacts } from '../../slices/business-profile/businessProfileSlice';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import { CardViewContent } from './components/card-view-content/CardViewContent';
import InfoIcon from '@mui/icons-material/Info';
import Preview from './components/preview-block/Preview';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';

const DocumentVaultBlock = () => {
  /********* To refactor **************/
  const { t } = useTranslation();
  const employees = useAppSelector(selectAllEmployees);
  const suppliers = useAppSelector(selectAllSuppliers);
  const customers = useAppSelector(selectAllCustomers);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const formUploadRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const handleUploadClose = () => {
    setIsUploadOpen(false);
  };

  const handleUploadOpen = () => {
    setIsUploadOpen(true);
  };

  const {
    documents,
    documentContractsLoading,
    documentUploadsLoading,
    customersLoading,
    suppliersLoading,
    employeesLoading,
    businessProfileLoading,
    documentsCount,
    currentPage,
    setCurrentPage,
    getRequestedPage,
    handleTabChange,
    activeTab,
    cardView,
    setCardView,
  } = useDocuments();
  const { pendingStatus: downloadPendingStatus } = useFileTypeDownload();
  // Items per page - adjust this value according to your API
  const itemsPerPage = 20;
  // Calculate total pages
  const totalPages = Math.ceil(documentsCount / itemsPerPage);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    getRequestedPage(page, activeTab);
  };

  /********* To refactor later*************/

  return (
    <>
      <Grid container spacing={3} sx={{ mt: '-78px' }}>
        <Grid size={{ xs: 12 }} sx={{ pt: '16px !important' }}>
          <Stack spacing={0} sx={{ marginBottom: '20px' }}>
            <Typography
              variant='h5'
              component='h1'
              sx={{ width: '50%', mb: '16px' }}
              className={styles.page_title}
            >
              {t('headers.documentVault')}
            </Typography>
            <Stack
              direction='row'
              alignItems={'center'}
              marginTop={'8px'}
              spacing={2}
              justifyContent={'space-between'}
            >
              <Box display='flex' alignItems={'center'} gap={'8px'}>
                <Box ref={uploadButtonRef}>
                  <UploadButton
                    disabled={
                      customersLoading === 'loading' ||
                      suppliersLoading === 'loading' ||
                      employeesLoading === 'loading' ||
                      businessProfileLoading === 'loading'
                    }
                    onClick={handleUploadOpen}
                    buttonText={t('buttons.uploadFiles')}
                    isArrowDown={false}
                  />
                </Box>
                {isUploadOpen && (
                  <Box className={styles.form_box} alignSelf={'flex-start'} ref={formUploadRef}>
                    <FormUpload
                      employees={employees}
                      suppliers={suppliers}
                      customers={customers}
                      businessContacts={businessContacts}
                      categoriesAsOption={true}
                      onCancel={handleUploadClose}
                      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                      // @ts-ignore
                      categoryValue={activeTab === 'All' ? '' : activeTab.toLowerCase()}
                    />
                  </Box>
                )}{' '}
                <PreviewTooltip title={<Preview />} arrow placement='top'>
                  <InfoIcon color='primary' fontSize='small' />
                </PreviewTooltip>
              </Box>
              <Box display='flex' gap={'8px'} alignItems={'center'}>
                {documents.length > 0 && (cardView ? activeTab !== 'All' : true) && (
                  <Box className='pagination-block' sx={{ display: 'flex', justifyContent: 'end' }}>
                    <Typography className='pagination-pages-value' variant='caption'>
                      Page {currentPage} of {totalPages}
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      siblingCount={0}
                      boundaryCount={0}
                      // don't enable first/last buttons
                      /*renderItem={(item) => {
                                                if (item.type === 'previous' || item.type === 'next') {
                                                  return <PaginationItem {...item} />;
                                                }
                                                // Render nothing for pages & ellipses so they don't exist in the DOM
                                                return null;
                                              }}*/
                      sx={{
                        // optional: tighten arrow spacing
                        '& .MuiPagination-ul': { gap: 0 },
                      }}
                    />
                  </Box>
                )}
                <Box className={styles.card_list_view_container}>
                  <button
                    className={`${styles.variant_btn} + ${
                      cardView ? styles.variant_btn_active : ''
                    }`}
                    onClick={() => setCardView(true)}
                  >
                    <DashboardIcon />
                    <span>{t('buttons.card')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setCardView(false);
                    }}
                    className={`${styles.variant_btn} + ${
                      !cardView ? styles.variant_btn_active : ''
                    }`}
                  >
                    <BallotIcon />
                    <span>{t('buttons.list')}</span>
                  </button>
                </Box>
              </Box>
            </Stack>
          </Stack>
          <Box className={styles.document_header}>
            <Box className={styles.actions_group}>
              {/* <Pagination page={page} totalPages={1} onPageChange={handlePageChange} />
              <SearchField
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder='Search for document'
              />
              <FilterDrawer sections={filterSections} onApplyFilters={handleApplyFilters} /> */}
            </Box>
          </Box>

          <Box className={styles.tab_container}>
            <Box className={styles.tabs_section}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => {
                  handleTabChange(newValue);
                }}
                aria-label='document categories'
                className={styles.tabs}
                style={{ marginBottom: 0 }}
              >
                <Tab label='All' value='All' />
                <Tab label='Business' value='Business' />
                <Tab label='Employment' value='Employment' />
                <Tab label='Customers' value='Customers' />
                <Tab label='Suppliers' value='Suppliers' />
              </Tabs>
            </Box>
          </Box>

          <Box sx={{ mt: '15px', position: 'relative' }}>
            {cardView ? (
              <>
                <CardViewContent
                  activeTab={activeTab}
                  changeTabFunction={handleTabChange}
                  documents={documents}
                  documentsCount={documentsCount}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  getRequestedPage={getRequestedPage}
                  /*setWorkflow={setWorkflow}
                                                  workflow={workflow}*/
                />
              </>
            ) : (
              <>
                {/* <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
                  <MuiPagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color='primary'
                  />
                </Box> */}
                <DocumentListVault documents={documents} />
                {/* Pagination component */}
              </>
            )}
            {(documentUploadsLoading === 'loading' ||
              documentContractsLoading === 'loading' ||
              customersLoading === 'loading' ||
              employeesLoading === 'loading' ||
              businessProfileLoading === 'loading' ||
              suppliersLoading === 'loading') && (
              <ProgressOverlay className={'position-absolute'} />
            )}
          </Box>
        </Grid>
      </Grid>
      {downloadPendingStatus && <ProgressOverlay />}
    </>
  );
};

export default DocumentVaultBlock;
